import { recordCallEvent } from '../../utils/callReliability/diagnostics';
import { restartDevice } from '../../utils/callReliability/devices';
import {
	SELECTED_AUDIO_INPUT_KEY,
	SELECTED_VIDEO_INPUT_KEY,
} from '../../constants';
import React, {
	createContext,
	ReactNode,
	useCallback,
	useState,
	useRef,
	useEffect,
} from 'react';
import {
	CreateLocalTrackOptions,
	ConnectOptions,
	LocalAudioTrack,
	LocalVideoTrack,
	Room,
} from 'twilio-video';
import { ErrorCallback } from '../../types';
import { SelectedParticipantProvider } from './useSelectedParticipant/useSelectedParticipant';

import Snackbar from '../Snackbar/Snackbar';
import AttachVisibilityHandler from './AttachVisibilityHandler/AttachVisibilityHandler';
import useBackgroundSettings, {
	BackgroundSettings,
} from './useBackgroundSettings/useBackgroundSettings';
import useHandleRoomDisconnection from './useHandleRoomDisconnection/useHandleRoomDisconnection';
import useHandleTrackPublicationFailed from './useHandleTrackPublicationFailed/useHandleTrackPublicationFailed';
import useLocalTracks from './useLocalTracks/useLocalTracks';
import useRestartAudioTrackOnDeviceChange from './useRestartAudioTrackOnDeviceChange/useRestartAudioTrackOnDeviceChange';
import useRoom from './useRoom/useRoom';
import useScreenShareToggle from './useScreenShareToggle/useScreenShareToggle';

/*
 *  The hooks used by the VideoProvider component are different than the hooks found in the 'hooks/' directory. The hooks
 *  in the 'hooks/' directory can be used anywhere in a video application, and they can be used any number of times.
 *  the hooks in the 'VideoProvider/' directory are intended to be used by the VideoProvider component only. Using these hooks
 *  elsewhere in the application may cause problems as these hooks should not be used more than once in an application.
 */

export interface IVideoContext {
	room: Room | null;
	localTracks: (LocalAudioTrack | LocalVideoTrack)[];
	isConnecting: boolean;
	connect: (token: string) => Promise<void>;
	onError: ErrorCallback;
	discardLocalTrack: (track: LocalAudioTrack | LocalVideoTrack) => void;
	getLocalAudioTrack: () => Promise<LocalAudioTrack>;
	restartAudioTrack: (options: CreateLocalTrackOptions) => Promise<void>;
	restartVideoTrack: (options: CreateLocalTrackOptions) => Promise<void>;
	backgroundStatus: 'off' | 'loading' | 'ready' | 'failed';
	cancelConnect: () => void;
	disconnectedUnexpectedly: boolean;
	getLocalVideoTrack: (
		newOptions?: CreateLocalTrackOptions,
	) => Promise<LocalVideoTrack>;
	isAcquiringLocalTracks: boolean;
	removeLocalVideoTrack: () => void;
	removeLocalAudioTrack: () => void;
	isSharingScreen: boolean;
	toggleScreenShare: () => void;
	getAudioAndVideoTracks: () => Promise<void>;
	isBackgroundSelectionOpen: boolean;
	setIsBackgroundSelectionOpen: (value: boolean) => void;
	backgroundSettings: BackgroundSettings;
	setBackgroundSettings: (settings: BackgroundSettings) => void;
}

export const VideoContext = createContext<IVideoContext>(null!);

interface VideoProviderProps {
	options?: ConnectOptions;
	onError: ErrorCallback;
	children: ReactNode;
}

export function VideoProvider({
	options: connectionOptions,
	children,
	onError = () => {},
}: VideoProviderProps) {
	const onErrorCallback: ErrorCallback = useCallback(
		(error) => {
			recordCallEvent('media-error', error.code);
			onError(error);
		},
		[onError],
	);

	const {
		localTracks,
		isCurrentTrack,
		getLocalVideoTrack: acquireVideoTrack,
		getLocalAudioTrack,
		isAcquiringLocalTracks,
		removeLocalAudioTrack,
		removeLocalVideoTrack,
		getAudioAndVideoTracks,
	} = useLocalTracks();
	const {
		room,
		isConnecting,
		connect: connectRoom,
		cancelConnect: cancelRoomConnect,
		disconnectedUnexpectedly,
	} = useRoom(localTracks, onErrorCallback, connectionOptions);

	const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(
		room,
		onError,
	);

	// Register callback functions to be called on room disconnect.
	useHandleRoomDisconnection(
		room,
		onError,
		removeLocalAudioTrack,
		useCallback(() => removeLocalVideoTrack(true), [removeLocalVideoTrack]),
		isSharingScreen,
		toggleScreenShare,
	);
	useHandleTrackPublicationFailed(room, onError);

	const [isBackgroundSelectionOpen, setIsBackgroundSelectionOpen] =
		useState(false);
	// Close the background panel whenever a call ends so it does not reappear
	// on the setup screen or on the next call.
	useEffect(() => {
		if (!room) setIsBackgroundSelectionOpen(false);
	}, [room]);
	const videoTrack = localTracks.find(
		(track) => !track.name.includes('screen') && track.kind === 'video',
	) as LocalVideoTrack | undefined;
	const [backgroundError, setBackgroundError] = useState<Error | null>(null);
	const [
		backgroundSettings,
		setBackgroundSettings,
		backgroundStatus,
		waitForBackground,
		refreshBackground,
	] = useBackgroundSettings(videoTrack, room, setBackgroundError);

	const current = useRef({ room, localTracks });
	current.current = { room, localTracks };
	const discardLocalTrack = useCallback(
		(track: LocalAudioTrack | LocalVideoTrack) => {
			const participant = current.current.room?.localParticipant;
			const publication = participant?.unpublishTrack(track);
			if (publication) participant?.emit('trackUnpublished', publication);
			if (isCurrentTrack(track)) {
				if (track.kind === 'audio') removeLocalAudioTrack();
				else removeLocalVideoTrack();
			} else track.stop();
		},
		[isCurrentTrack, removeLocalAudioTrack, removeLocalVideoTrack],
	);
	const getLocalVideoTrack = useCallback(
		async (options?: CreateLocalTrackOptions) => {
			const track = await acquireVideoTrack(options);
			try {
				await waitForBackground(track);
				return track;
			} catch (error) {
				discardLocalTrack(track);
				throw error;
			}
		},
		[acquireVideoTrack, waitForBackground, discardLocalTrack],
	);
	const joinGeneration = useRef(0);
	const cancelConnect = useCallback(() => {
		joinGeneration.current++;
		cancelRoomConnect();
	}, [cancelRoomConnect]);
	const connect = useCallback(
		async (token: string) => {
			const attempt = joinGeneration.current;
			if (videoTrack) await waitForBackground(videoTrack);
			if (attempt !== joinGeneration.current)
				throw new Error('Joining cancelled.');
			await connectRoom(token);
		},
		[videoTrack, waitForBackground, connectRoom],
	);
	useEffect(() => {
		recordCallEvent(
			`background-${backgroundSettings.type}-${backgroundStatus}`,
		);
	}, [backgroundSettings.type, backgroundStatus, room]);
	const restartAudioTrack = useCallback(
		async (options: CreateLocalTrackOptions) => {
			const track = current.current.localTracks.find(
				(item) => item.kind === 'audio',
			) as LocalAudioTrack | undefined;
			if (!track) return;
			const active = () => isCurrentTrack(track) && !track.isStopped;
			await restartDevice(track, options, () => isCurrentTrack(track));
			if (active())
				localStorage.setItem(
					SELECTED_AUDIO_INPUT_KEY,
					(
						track.noiseCancellation?.sourceTrack ||
						track.mediaStreamTrack
					).getSettings().deviceId || '',
				);
		},
		[isCurrentTrack],
	);
	const videoOperation = useRef<Promise<void>>();
	const restartVideoTrack = useCallback(
		(options: CreateLocalTrackOptions) => {
			if (videoOperation.current) return videoOperation.current;
			const track = current.current.localTracks.find(
				(item) => item.kind === 'video',
			) as LocalVideoTrack | undefined;
			if (!track) {
				// Selecting a camera while it is off must not turn it on.
				const deviceId = options.deviceId as
					{ exact?: string } | undefined;
				if (deviceId?.exact)
					localStorage.setItem(
						SELECTED_VIDEO_INPUT_KEY,
						deviceId.exact,
					);
				return Promise.resolve();
			}
			const call = current.current.room;
			const active = () =>
				isCurrentTrack(track) &&
				(!call || call.state !== 'disconnected');
			videoOperation.current = (async () => {
				const publication =
					call?.localParticipant.unpublishTrack(track);
				if (publication)
					call?.localParticipant.emit(
						'trackUnpublished',
						publication,
					);
				await restartDevice(track, options, active);
				if (!active()) return;
				refreshBackground();
				await waitForBackground(track);
				if (!active()) {
					track.stop();
					return;
				}
				localStorage.setItem(
					SELECTED_VIDEO_INPUT_KEY,
					track.mediaStreamTrack.getSettings().deviceId || '',
				);
				await call?.localParticipant.publishTrack(track, {
					priority: 'low',
				});
			})()
				.catch((error) => {
					if (
						active() &&
						(track.isStopped ||
							track.mediaStreamTrack.readyState === 'ended')
					)
						discardLocalTrack(track);
					throw error;
				})
				.finally(() => {
					videoOperation.current = undefined;
				});
			return videoOperation.current;
		},
		[
			refreshBackground,
			waitForBackground,
			isCurrentTrack,
			discardLocalTrack,
		],
	);
	useRestartAudioTrackOnDeviceChange(
		localTracks,
		restartAudioTrack,
		restartVideoTrack,
		onErrorCallback,
	);

	return (
		<VideoContext.Provider
			value={{
				room,
				localTracks,
				isConnecting,
				onError: onErrorCallback,
				getLocalVideoTrack,
				getLocalAudioTrack,
				discardLocalTrack,
				restartAudioTrack,
				restartVideoTrack,
				backgroundStatus,
				cancelConnect,
				disconnectedUnexpectedly,
				connect,
				isAcquiringLocalTracks,
				removeLocalVideoTrack,
				removeLocalAudioTrack,
				isSharingScreen,
				toggleScreenShare,
				getAudioAndVideoTracks,
				isBackgroundSelectionOpen,
				setIsBackgroundSelectionOpen,
				backgroundSettings,
				setBackgroundSettings,
			}}
		>
			<SelectedParticipantProvider room={room}>
				{children}
			</SelectedParticipantProvider>
			{/* 
        The AttachVisibilityHandler component is using the useLocalVideoToggle hook
        which must be used within the VideoContext Provider.
      */}
			<AttachVisibilityHandler />
			<Snackbar
				open={Boolean(backgroundError)}
				headline="Background effect turned off"
				message={backgroundError?.message || ''}
				variant="warning"
				handleClose={() => setBackgroundError(null)}
			/>
		</VideoContext.Provider>
	);
}
